import {
  Box,
  Flex,
  VStack,
  Text,
  IconButton,
  Input,
  Textarea,
  Spinner,
  Tooltip,
  Collapse,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { FaTrash, FaEdit, FaRedo, FaCheck, FaTimes, FaChevronUp, FaChevronDown } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import { InsightsProto } from "build-ts/analysis";

interface InsightComponentProps {
  insight: InsightsProto;
  index: number;
  isLoading: boolean;
  onEditQuestion?: (index: number, newQuestion: string) => void;
  onEditAnswer?: (index: number, newAnswer: string) => void;
  onDeleteQuestion?: (index: number) => void;
  onRegenerateAnswer?: (index: number, question: string) => void;
}

const InsightComponent: React.FC<InsightComponentProps> = ({
  insight,
  index,
  isLoading,
  onEditQuestion,
  onEditAnswer,
  onDeleteQuestion,
  onRegenerateAnswer,
}) => {
  const [isEditingQuestion, setIsEditingQuestion] = useState<boolean>(false);
  const [isEditingAnswer, setIsEditingAnswer] = useState<boolean>(false);
  const [editQuestionText, setEditQuestionText] = useState<string>("");
  const [editAnswerText, setEditAnswerText] = useState<string>("");
  const [isAnswerCollapsed, setIsAnswerCollapsed] = useState<boolean>(true);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const question = insight.question || "";
  const answer = insight.answer || "";

  // Keep answers collapsed by default
  useEffect(() => {
    if (isLoading === true) {
      setIsAnswerCollapsed(true);
    }
  }, [isLoading]);

  // Edit handlers for questions
  const handleStartEditQuestion = () => {
    setEditQuestionText(question);
    setIsEditingQuestion(true);
  };

  const handleSaveQuestion = () => {
    if (editQuestionText.trim()) {
      onEditQuestion?.(index, editQuestionText.trim());
    }
    setIsEditingQuestion(false);
    setEditQuestionText("");
  };

  const handleCancelEditQuestion = () => {
    setIsEditingQuestion(false);
    setEditQuestionText("");
  };

  // Edit handlers for answers
  const handleStartEditAnswer = () => {
    setEditAnswerText(answer);
    setIsEditingAnswer(true);
  };

  const handleSaveAnswer = () => {
    onEditAnswer?.(index, editAnswerText);
    setIsEditingAnswer(false);
    setEditAnswerText("");
  };

  const handleCancelEditAnswer = () => {
    setIsEditingAnswer(false);
    setEditAnswerText("");
  };

  const toggleAnswerCollapse = () => {
    setIsAnswerCollapsed(!isAnswerCollapsed);
  };

  return (
    <Box
      p={3}
      borderRadius="md"
      border="1px solid"
      borderColor="gray.100"
      bg="white"
      _hover={{
        bg: "gray.50",
        borderColor: "purple.300",
        transform: "translateY(-1px)",
        shadow: "md",
      }}
      transition="all 0.2s ease"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <VStack spacing={2} align="stretch">
        {/* Question */}
        {isEditingQuestion ? (
          <VStack spacing={2} align="stretch">
            <Input
              value={editQuestionText}
              onChange={(e) => setEditQuestionText(e.target.value)}
              size="sm"
              fontSize="sm"
              placeholder="Enter question..."
              autoFocus
              borderColor="gray.200"
              _focus={{ borderColor: "purple.500", boxShadow: "none" }}
            />
            <Flex justify="flex-end" gap={1}>
              <Tooltip label="Save" placement="top">
                <IconButton
                  aria-label="Save question"
                  icon={<FaCheck size="0.8em" />}
                  size="xs"
                  colorScheme="purple"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveQuestion();
                  }}
                  isDisabled={!editQuestionText.trim()}
                />
              </Tooltip>
              <Tooltip label="Cancel" placement="top">
                <IconButton
                  aria-label="Cancel edit"
                  icon={<FaTimes size="0.8em" />}
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelEditQuestion();
                  }}
                />
              </Tooltip>
            </Flex>
          </VStack>
        ) : question ? (
          <Flex
            w="100%"
            justify="flex-start"
            align="flex-start"
            gap={2}
          >
            <Text
              fontSize="sm"
              color="gray.700"
              fontWeight="500"
              lineHeight="1.5"
              flex="1"
              textAlign="left"
            >
              <Text
                as="span"
                color="purple.600"
                fontWeight="600"
                mr={1}
              >
                Q:
              </Text>
              {question}
            </Text>
            <Flex gap={1}>
              <Tooltip label="Edit question" placement="top">
                <IconButton
                  aria-label="Edit question"
                  icon={<FaEdit size="0.8em" />}
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartEditQuestion();
                  }}
                  color="gray.500"
                  _hover={{ color: "purple.500" }}
                  opacity={isHovered ? 1 : 0}
                  transition="opacity 0.2s ease"
                />
              </Tooltip>
              <Tooltip label="Delete question" placement="top">
                <IconButton
                  aria-label="Delete question"
                  icon={<FaTrash size="0.8em" />}
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteQuestion?.(index);
                  }}
                  color="red.400"
                  _hover={{ color: "red.500" }}
                  opacity={isHovered ? 1 : 0}
                  transition="opacity 0.2s ease"
                />
              </Tooltip>
            </Flex>
          </Flex>
        ) : null}

        {/* Answer Section with Collapse Toggle */}
        {question && (
          <Box>
            {/* Toggle Button */}
            <Flex justify="space-between" align="center" mb={isAnswerCollapsed ? 0 : 2}>
              <Tooltip label={isAnswerCollapsed ? "Expand answer" : "Collapse answer"} placement="top">
                <IconButton
                  aria-label={isAnswerCollapsed ? "Expand answer" : "Collapse answer"}
                  icon={isAnswerCollapsed ? <FaChevronDown size="0.8em" /> : <FaChevronUp size="0.8em" />}
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAnswerCollapse();
                  }}
                  color="gray.500"
                  _hover={{ color: "purple.500" }}
                />
              </Tooltip>
              {isAnswerCollapsed && (
                <Text fontSize="xs" color="gray.500" fontStyle="italic">
                  Answer {answer ? "hidden" : isLoading ? "loading..." : "not available"}
                  {isLoading && <Spinner size="xs" color="purple.500" ml={1} />}
                </Text>
              )}
            </Flex>

            {/* Collapsible Answer Content */}
            <Collapse in={!isAnswerCollapsed} animateOpacity>
              {/* Answer */}
              {isEditingAnswer ? (
                <VStack spacing={2} align="stretch">
                  <Textarea
                    value={editAnswerText}
                    onChange={(e) => setEditAnswerText(e.target.value)}
                    size="sm"
                    fontSize="sm"
                    placeholder="Enter answer..."
                    rows={3}
                    autoFocus
                    borderColor="gray.200"
                    _focus={{ borderColor: "purple.500", boxShadow: "none" }}
                  />
                  <Flex justify="flex-end" gap={1}>
                    <Tooltip label="Save" placement="top">
                      <IconButton
                        aria-label="Save answer"
                        icon={<FaCheck size="0.8em" />}
                        size="xs"
                        colorScheme="purple"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveAnswer();
                        }}
                      />
                    </Tooltip>
                    <Tooltip label="Cancel" placement="top">
                      <IconButton
                        aria-label="Cancel edit"
                        icon={<FaTimes size="0.8em" />}
                        size="xs"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelEditAnswer();
                        }}
                      />
                    </Tooltip>
                  </Flex>
                </VStack>
              ) : (
                <Flex
                  w="100%"
                  justify="flex-start"
                  align="flex-start"
                  gap={2}
                >
                  <Box flex="1">
                    {answer ? (
                      <Text
                        fontSize="sm"
                        color="gray.700"
                        lineHeight="1.5"
                        textAlign="left"
                      >
                        <Text
                          as="span"
                          color="green.600"
                          fontWeight="600"
                          mr={1}
                        >
                          A:
                        </Text>
                        <ReactMarkdown
                          components={{
                            ul: ({node, ...props}) => (
                              <ul style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
                            ),
                            ol: ({node, ...props}) => (
                              <ol style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
                            ),
                            li: ({node, ...props}) => (
                              <li style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }} {...props} />
                            ),
                            p: ({node, ...props}) => (
                              <p style={{ fontSize: "0.875rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
                            ),
                            h1: ({node, ...props}) => (
                              <h1 style={{ fontSize: "1.15rem", fontWeight: "bold", margin: "1rem 0" }} {...props} />
                            ),
                            h2: ({node, ...props}) => (
                              <h2 style={{ fontSize: "1.05rem", fontWeight: "bold", margin: "0.875rem 0" }} {...props} />
                            ),
                            h3: ({node, ...props}) => (
                              <h3 style={{ fontSize: "0.95rem", fontWeight: "bold", margin: "0.75rem 0" }} {...props} />
                            ),
                            h4: ({node, ...props}) => (
                              <h4 style={{ fontSize: "0.85rem", fontWeight: "bold", margin: "0.625rem 0" }} {...props} />
                            ),
                          }}
                        >
                          {answer}
                        </ReactMarkdown>
                      </Text>
                    ) : isLoading ? (
                      <Flex align="flex-start" gap={2}>
                        <Text
                          fontSize="sm"
                          color="purple.500"
                          fontStyle="italic"
                          textAlign="left"
                        >
                          <Text
                            as="span"
                            fontWeight="600"
                            mr={1}
                          >
                            A:
                          </Text>
                          Generating answer...
                        </Text>
                        <Box pt={0}>
                          <Spinner size="xs" color="purple.500" />
                        </Box>
                      </Flex>
                    ) : (
                      <Text
                        fontSize="sm"
                        color="gray.400"
                        fontStyle="italic"
                        textAlign="left"
                      >
                        <Text
                          as="span"
                          fontWeight="600"
                          mr={1}
                        >
                          A:
                        </Text>
                        No answer available
                      </Text>
                    )}
                  </Box>
                  <Flex gap={1}>
                    <Tooltip label="Edit answer" placement="top">
                      <IconButton
                        aria-label="Edit answer"
                        icon={<FaEdit size="0.8em" />}
                        size="xs"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartEditAnswer();
                        }}
                        color="gray.500"
                        _hover={{ color: "purple.500" }}
                        opacity={isHovered ? 1 : 0}
                        transition="opacity 0.2s ease"
                      />
                    </Tooltip>
                    {question && onRegenerateAnswer && (
                      <Tooltip label="Regenerate answer" placement="top">
                        <IconButton
                          aria-label="Regenerate answer"
                          icon={isLoading ? <Spinner size="xs" /> : <FaRedo size="0.8em" />}
                          size="xs"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isLoading) {
                              onRegenerateAnswer(index, question);
                            }
                          }}
                          color="purple.500"
                          _hover={{ color: "purple.600" }}
                          isDisabled={isLoading}
                          cursor={isLoading ? "not-allowed" : "pointer"}
                          opacity={isHovered ? 1 : 0}
                          transition="opacity 0.2s ease"
                        />
                      </Tooltip>
                    )}
                  </Flex>
                </Flex>
              )}
            </Collapse>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default InsightComponent; 